import React, { useEffect, useState, useRef } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Space,
    DatePicker,
    message,
    Typography,
    Divider
} from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import { adminAPI } from '../../utils/api';
import dayjs from 'dayjs';
import FingerprintCapture from '../Biometric/FingerprintCapture';

const { Option } = Select;
const { Title } = Typography;

const ConsumerEditModal = ({ visible, onCancel, onSuccess, consumer }) => {
    const [coaches, setCoaches] = useState([]);
    const [newCoachName, setNewCoachName] = useState('');
    const [addingCoach, setAddingCoach] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 🧬 100% RELIABLE storage: Use a Ref to ensure we never lose templates to stale cycles
    const templatesRef = useRef([null, null]);
    const [isCaptured, setIsCaptured] = useState([false, false]);

    useEffect(() => {
        if (visible) {
            fetchCoaches();
        }
    }, [visible]);

    const fetchCoaches = async () => {
        try {
            const response = await adminAPI.getCoaches();
            setCoaches(response.data.coaches || []);
        } catch (error) {
            console.error('Failed to fetch coaches');
        }
    };

    const extractBiometricData = (xmlString) => {
        if (!xmlString) return null;
        console.log('🧬 RAW BIOMETRIC XML:', xmlString);
        
        // Primary Attempt: standard <Data> tag
        const dataMatch = xmlString.match(/<Data[^>]*>([^<]+)<\/Data>/);
        if (dataMatch && dataMatch[1].trim().length > 10) {
            return dataMatch[1].trim();
        }

        // Secondary Attempt: find largest block of base64-like characters
        const base64Blocks = xmlString.match(/[A-Za-z0-9+/]{50,}/g);
        if (base64Blocks && base64Blocks.length > 0) {
            // Sort by length and take the longest one (the template)
            return base64Blocks.sort((a, b) => b.length - a.length)[0];
        }

        return null;
    };

    const handleFingerprintCapture = (index, xmlTemplate) => {
        const base64Data = extractBiometricData(xmlTemplate);
        if (!base64Data) {
            message.error(`Failed to extract data from Finger ${index + 1}. Try again.`);
            console.warn('Extraction failed for XML:', xmlTemplate);
            return;
        }

        // ✅ 1. Store in Ref (Rock Solid)
        templatesRef.current[index] = base64Data;

        // ✅ 2. Update local state (UI Feedback)
        setIsCaptured(prev => {
            const next = [...prev];
            next[index] = true;
            return next;
        });

        // ✅ 3. Update Form (for sync)
        const fieldName = `fingerprint_template_${index + 1}`;
        form.setFieldsValue({ [fieldName]: base64Data });
        
        message.success(`Finger ${index + 1} captured!`);
    };

    const handleAddCoach = async (e) => {
        e.preventDefault();
        if (!newCoachName.trim()) return;

        try {
            setAddingCoach(true);
            const response = await adminAPI.createCoach({ name: newCoachName });
            message.success('Coach added successfully');
            setNewCoachName('');
            await fetchCoaches();
            form.setFieldsValue({ coach_name: response.data.coach.name });
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to add coach');
        } finally {
            setAddingCoach(false);
        }
    };

    useEffect(() => {
        if (visible) {
            if (consumer) {
                const consumerData = {
                    ...consumer,
                    date_of_birth: consumer.date_of_birth ? dayjs(consumer.date_of_birth) : null,
                    anniversary_date: consumer.anniversary_date ? dayjs(consumer.anniversary_date) : null,
                    start_date: consumer.start_date ? dayjs(consumer.start_date) : dayjs(),
                    fingerprint_template_1: consumer.fingerprint_template_1 || null,
                    fingerprint_template_2: consumer.fingerprint_template_2 || null
                };
                form.setFieldsValue(consumerData);
                templatesRef.current = [
                    consumer.fingerprint_template_1 || null,
                    consumer.fingerprint_template_2 || null
                ];
                setIsCaptured([!!consumer.fingerprint_template_1, !!consumer.fingerprint_template_2]);
            } else {
                form.resetFields();
                form.setFieldsValue({
                    start_date: dayjs(),
                    gender: 'male',
                    is_active: true
                });
                templatesRef.current = [null, null];
                setIsCaptured([false, false]);
            }
        }
    }, [visible, consumer, form]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // ✅ Manual merge for 100% reliability
            const consumerData = {
                ...values,
                date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : undefined,
                anniversary_date: values.anniversary_date ? values.anniversary_date.format('YYYY-MM-DD') : undefined,
                start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                is_active: values.is_active !== undefined ? values.is_active : true,
                fingerprint_template_1: templatesRef.current[0],
                fingerprint_template_2: templatesRef.current[1]
            };

            console.log('🚀 FINAL PAYLOAD:', {
                name: consumerData.name,
                has_fp1: !!consumerData.fingerprint_template_1,
                has_fp2: !!consumerData.fingerprint_template_2,
                fp1_length: consumerData.fingerprint_template_1?.length || 0
            });

            if (consumer) {
                await adminAPI.updateConsumer(consumer.id, consumerData);
                message.success('Consumer updated successfully');
            } else {
                await adminAPI.createConsumer(consumerData);
                message.success('Consumer added successfully');
            }

            onSuccess();
        } catch (error) {
            console.error(error);
            message.error(consumer ? 'Failed to update consumer' : 'Failed to add consumer');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={consumer ? "Edit Consumer" : "Add New Consumer"}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            style={{ top: 20 }}
            destroyOnClose={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                {/* 🧬 Biometric Enrollment - Top Section */}
                <div style={{
                    background: '#f0f7ff',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #bae7ff',
                    marginBottom: '24px'
                }}>
                    <Title level={5} style={{ color: '#0050b3', marginBottom: '8px' }}>
                        <SafetyOutlined style={{ marginRight: 8 }} />
                        Biometric Enrollment
                    </Title>
                    <p style={{ fontSize: '13px', color: '#444', marginBottom: '12px' }}>
                        Scan fingerprints below. These will be used for automatic check-ins.
                    </p>
                    
                    <Form.Item name="fingerprint_template_1" noStyle><Input type="hidden" /></Form.Item>
                    <Form.Item name="fingerprint_template_2" noStyle><Input type="hidden" /></Form.Item>

                    <Form.Item noStyle shouldUpdate>
                        {() => (
                            <FingerprintCapture
                                capturedTemplates={[
                                    form.getFieldValue('fingerprint_template_1'),
                                    form.getFieldValue('fingerprint_template_2')
                                ]}
                                onCapture={handleFingerprintCapture}
                                disabled={loading}
                            />
                        )}
                    </Form.Item>
                </div>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="name"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter consumer name' }]}
                        >
                            <Input placeholder="Enter full name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[{ type: 'email', message: 'Invalid email' }]}
                        >
                            <Input placeholder="Enter email (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="phone" label="Phone Number">
                            <Input placeholder="Enter phone number (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="gender"
                            label="Gender"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Select placeholder="Select gender">
                                <Option value="male">Male</Option>
                                <Option value="female">Female</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="plan_type"
                            label="Plan Type"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Select placeholder="Select plan type">
                                <Option value="sample">Sample User</Option>
                                <Option value="3_day">3 Day Plan</Option>
                                <Option value="membership">Membership</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="coach_name" label="Coach Name">
                            <Select
                                placeholder="Select or add coach"
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ padding: '0 8px 4px' }}>
                                            <Space>
                                                <Input
                                                    placeholder="New Coach Name"
                                                    value={newCoachName}
                                                    onChange={e => setNewCoachName(e.target.value)}
                                                    onKeyDown={e => e.stopPropagation()}
                                                    style={{ width: 156 }}
                                                />
                                                <Button type="primary" onClick={handleAddCoach} loading={addingCoach} size="small">
                                                    + Add
                                                </Button>
                                            </Space>
                                        </div>
                                    </>
                                )}
                            >
                                {coaches.map(coach => (
                                    <Option key={coach.id} value={coach.name}>{coach.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="added_via" label="Added Via">
                            <Select placeholder="Select how consumer was added" allowClear>
                                <Option value="prime_time">Prime Time</Option>
                                <Option value="physical">Physical</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="date_of_birth" label="Date of Birth">
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="DOB" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="anniversary_date" label="Anniversary Date">
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Anniversary" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="start_date"
                            label="Start Date"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="address" label="Address">
                            <Input.TextArea rows={2} placeholder="Optional address" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {consumer ? "Update Consumer" : "Add Consumer"}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ConsumerEditModal;
